import { REQUEST_STATUS_OPTIONS, TABLES } from '@/utils/constants';
import {
  getRecord,
  getRecords,
  insertRecord,
  updateRecord
} from '@/utils/helpers/data';
import { supabase } from '@/utils/supabase';
import { AttendanceStatus, RequestStatus } from '@/utils/types';

export const COLUMNS = {
  ID: 'id',
  DATE: 'date',
  START_TIME: 'start_time',
  END_TIME: 'end_time',
  PURPOSE: 'purpose',
  STATUS: 'status',
  CREATED_AT: 'created_at',
  UPDATED_AT: 'updated_at',
  DENTIST_ID: 'dentist_id',
  PATIENT_ID: 'patient_id',
  CANCEL_REASON: 'cancel_reason',
  CODE: 'code',
  RESCHEDULE_REASON: 'reschedule_reason',
  DECLINE_REASON: 'decline_reason',
  ATTENDANCE_STATUS: 'attendance_status'
};

/**
 * Represents a request made by a patient or dentist.
 */
export interface Request {
  id?: string;
  date: string | null;
  start_time: string | null;
  end_time: string | null;
  purpose: string | null;
  status: RequestStatus;
  created_at?: string;
  updated_at?: string;
  dentist_id: string | null;
  patient_id: string | null;
  cancel_reason?: string | null;
  code?: string | null;
  reschedule_reason?: string | null;
  decline_reason?: string | null;
  attendance_status?: AttendanceStatus | null;
}

/**
 * Creates a new request record in the database.
 * @param request - The request data to insert.
 * @returns The created request or null if insertion failed.
 * @throws Will throw an error if the insertion fails.
 */
export const createRequest = async (
  request: Request
): Promise<Request | null> => {
  if (!Object.values(REQUEST_STATUS_OPTIONS).includes(request.status))
    return null;

  return insertRecord<Request>(TABLES.REQUESTS, request);
};

/**
 * Updates an existing request record in the database.
 * @param params - Contains the request ID and the updated request data.
 * @returns The updated request or null if update failed.
 * @throws Will throw an error if the update fails.
 */
export const updateRequest = async ({
  requestId,
  request
}: {
  requestId: string | null | undefined;
  request: Request;
}): Promise<Request | null> => {
  if (!requestId) return null;

  if (!Object.values(REQUEST_STATUS_OPTIONS).includes(request.status))
    return null;

  return updateRecord<Request>(TABLES.REQUESTS, COLUMNS.ID, requestId, request);
};

/**
 * Retrieves a single request by its ID.
 * @param requestId - The ID of the request to retrieve.
 * @returns The requested record or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getRequest = async (
  requestId: string | null | undefined
): Promise<Request | null> => {
  if (!requestId) return null;

  return getRecord<Request>(TABLES.REQUESTS, COLUMNS.ID, requestId);
};

/**
 * Retrieves a single request by its code.
 * @param code - The code of the request to retrieve.
 * @returns The requested record or null if not found.
 * @throws Will throw an error if the retrieval fails.
 */
export const getRequestByCode = async (
  code: string | null | undefined
): Promise<Request | null> => {
  if (!code) return null;

  return getRecord<Request>(TABLES.REQUESTS, COLUMNS.CODE, code);
};

/**
 * Retrieves all requests associated with a specific dentist ID.
 * @param dentistId - The ID of the dentist.
 * @returns An array of requests.
 * @throws Will throw an error if the retrieval fails.
 */
export const getRequestsByDentistId = async (
  dentistId: string | null | undefined
): Promise<Request[]> => {
  if (!dentistId) return [];

  return getRecords<Request>(TABLES.REQUESTS, COLUMNS.DENTIST_ID, dentistId);
};

/**
 * Retrieves all requests for a specific dentist ID and status.
 * @param dentistId - The ID of the dentist.
 * @param status - The status of the requests.
 * @returns An array of requests matching the criteria.
 * @throws Will throw an error if the retrieval fails.
 */
export const getRequestsByDentistIdAndStatus = async (
  dentistId: string | null | undefined,
  status: string
): Promise<Request[]> => {
  if (!dentistId || !status) return [];
  if (!Object.values(REQUEST_STATUS_OPTIONS).includes(status)) return [];

  const { data, error } = await supabase
    .from(TABLES.REQUESTS)
    .select('*')
    .eq(COLUMNS.DENTIST_ID, dentistId)
    .eq(COLUMNS.STATUS, status);

  if (error) throw new Error(error.message);

  return data ?? [];
};

/**
 * Retrieves all requests associated with a specific patient ID and status.
 * @param patientId - The ID of the patient.
 * @param status - The status of the requests.
 * @returns An array of requests matching the criteria.
 * @throws Will throw an error if the retrieval fails.
 */
export const getRequestsByPatientIdAndStatus = async (
  patientId: string | null | undefined,
  status: string
): Promise<Request[]> => {
  if (!patientId || !status) return [];
  if (!Object.values(REQUEST_STATUS_OPTIONS).includes(status)) return [];

  const { data, error } = await supabase
    .from(TABLES.REQUESTS)
    .select('*')
    .eq(COLUMNS.PATIENT_ID, patientId)
    .eq(COLUMNS.STATUS, status);

  if (error) throw new Error(error.message);

  return data ?? [];
};

/**
 * Retrieves all requests based on patient ID, dentist ID, and status.
 * @param params - Contains patientId, dentistId, and status.
 * @returns An array of requests matching the criteria.
 * @throws Will throw an error if the retrieval fails.
 */
export const getRequestsByReferenceAndStatus = async ({
  patientId,
  dentistId,
  status
}: {
  patientId: string | null | undefined;
  dentistId: string | null | undefined;
  status: string | null | undefined;
}): Promise<Request[]> => {
  if (!patientId || !dentistId || !status) return [];
  if (!Object.values(REQUEST_STATUS_OPTIONS).includes(status)) return [];

  const { data, error } = await supabase
    .from(TABLES.REQUESTS)
    .select('*')
    .eq(COLUMNS.PATIENT_ID, patientId)
    .eq(COLUMNS.DENTIST_ID, dentistId)
    .eq(COLUMNS.STATUS, status);

  if (error) throw new Error(error.message);

  return data ?? [];
};
